import { useCallback, useMemo, useReducer } from 'react';

import { useTranslation } from 'react-i18next';

import {
  QCFlowEvents,
  QCFlowStates,
  getNextQcFlowState,
  isQcFlowEventAllowed,
} from '@contexts/operator/qcFlowMachine';

const initialState = {
  state: QCFlowStates.AWAITING_ORDER,
  previousState: null,
  lastEvent: null,
  context: {},
};

function reducer(state, action) {
  switch (action.type) {
    case 'TRIGGER': {
      const { event, payload } = action.payload;
      const nextState = getNextQcFlowState(state.state, event);

      if (!nextState) {
        if (import.meta.env.DEV) {
           
          console.warn('[QC_FLOW] Invalid transition', {
            currentState: state.state,
            event,
          });
        }
        return state;
      }

      return {
        state: nextState,
        previousState: state.state,
        lastEvent: event,
        context: { ...state.context, ...payload },
      };
    }
    case 'RESET': {
      const { payload } = action;
      return {
        state: QCFlowStates.AWAITING_ORDER,
        previousState: null,
        lastEvent: null,
        context: payload || {},
      };
    }
    default:
      return state;
  }
}

export default function useQcFlowMachine(initialContext = {}) {
  const { t } = useTranslation();
  const [flowState, dispatch] = useReducer(reducer, {
    ...initialState,
    context: { ...initialState.context, ...initialContext },
  });

  const trigger = useCallback((event, payload = {}) => {
    dispatch({ type: 'TRIGGER', payload: { event, payload } });
  }, []);

  const reset = useCallback((payload = {}) => {
    dispatch({ type: 'RESET', payload });
  }, []);

  const canTrigger = useCallback(
    (event) => isQcFlowEventAllowed(flowState.state, event),
    [flowState.state],
  );

  const label = useMemo(
    () => t(`operator.qc.flow_state.${flowState.state}`, { defaultValue: flowState.state }),
    [flowState.state, t],
  );

  return {
    state: flowState.state,
    previousState: flowState.previousState,
    lastEvent: flowState.lastEvent,
    context: flowState.context,
    label,
    trigger,
    canTrigger,
    reset,
    QCFlowStates,
    QCFlowEvents,
  };
}
